<template>
  <div id="users">
    <!-- export confirmation -->
    <DialogContainer v-if="confirmExport">
      <div class="adm-dialog">
        <h3>Export App Users</h3>
        <p>Download the current filtered list as a CSV?</p>
        <div class="adm-dialog-actions">
          <button class="adm-btn-ghost" @click="no">Cancel</button>
          <button class="adm-btn-primary" @click="yes">Export</button>
        </div>
      </div>
    </DialogContainer>

    <DialogContainer v-if="exportToCsv">
      <div class="adm-dialog">
        <i class="fa fa-download adm-dialog-icon" />
        <p>Exporting users data…</p>
        <img src="../assets/admin_loading.gif" alt="Loading" class="loader" />
        <button class="adm-btn-ghost" @click="toggleExportDialog">Cancel</button>
      </div>
    </DialogContainer>

    <!-- delete confirmation -->
    <DialogContainer v-if="deleteTarget">
      <div class="adm-dialog">
        <h3 class="text-danger">Delete User Account</h3>
        <p>
          Permanently delete <strong>{{ deleteTarget.email }}</strong>?
          This also removes their devices, glucose readings, and tokens.
          <br /><em>This action cannot be undone.</em>
        </p>
        <div class="adm-dialog-actions">
          <button class="adm-btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="adm-btn-primary danger" @click="performDelete">Delete</button>
        </div>
      </div>
    </DialogContainer>

    <div class="adm-toolbar">
      <input
        v-model="textInput"
        placeholder="Search id or email…"
        class="adm-input search-input"
        aria-label="Search"
      />
      <div class="adm-toolbar-spacer" />
      <button
        v-if="users.length > 0"
        type="button"
        class="adm-btn-primary"
        @click="exportBtn"
      >
        <i class="fa fa-download" /> Export
      </button>
    </div>

    <div v-if="isLoading" class="adm-empty">
      <img src="../assets/admin_loading.gif" alt="Loading" class="loader" />
      <div class="adm-empty-sub">Loading users…</div>
    </div>

    <div v-else-if="users.length > 0" class="adm-card">
      <b-table
        id="users-table"
        :items="users"
        :fields="fields"
        hover
        small
        responsive
        :per-page="perPage"
        :current-page="currentPage"
        class="adm-table"
      >
        <template #cell(role)="data">
          <div class="role-cell">
            <b-dropdown
              v-if="isAdmin"
              right
              no-caret
              variant="link"
              size="sm"
              toggle-class="role-trigger"
              menu-class="adm-menu"
              boundary="viewport"
              :disabled="!!data.item._roleSaving || !data.item.is_active"
              class="role-dropdown"
            >
              <template #button-content>
                <span>{{ roleLabel(data.item.role) }}</span>
                <i class="fa fa-chevron-down role-caret" />
              </template>
              <b-dropdown-item
                v-for="opt in roleOptions"
                :key="opt.value"
                :active="opt.value === (data.item.role || 'user')"
                @click="changeRole(data.item, opt.value)"
              >
                {{ opt.text }}
              </b-dropdown-item>
            </b-dropdown>
            <span v-else>{{ roleLabel(data.item.role) }}</span>
            <i
              v-if="data.item._roleSaving"
              class="fa fa-spinner fa-spin role-saving"
            />
          </div>
        </template>
        <template #cell(is_active)="data">
          <span
            class="adm-status"
            :class="data.item.is_active ? 'adm-status-active' : 'adm-status-inactive'"
          >
            {{ data.item.is_active ? "Active" : "Inactive" }}
          </span>
        </template>
        <template #cell(actions)="data">
          <b-dropdown
            right
            no-caret
            variant="link"
            size="sm"
            toggle-class="actions-trigger"
            menu-class="adm-menu"
            boundary="viewport"
            class="row-action-dropdown"
          >
            <template #button-content>
              <span>Actions</span>
              <i class="fa fa-chevron-down actions-caret" />
            </template>
            <b-dropdown-item
              v-if="data.item.is_active"
              @click="deactivateUser(data.item)"
              >Deactivate</b-dropdown-item
            >
            <b-dropdown-item v-else @click="activateUser(data.item)"
              >Activate</b-dropdown-item
            >
            <b-dropdown-item
              class="text-danger"
              @click="confirmDeleteUser(data.item)"
              >Delete</b-dropdown-item
            >
          </b-dropdown>
        </template>
      </b-table>

      <div v-if="users.length > perPage" class="adm-pagination">
        <b-pagination
          v-model="currentPage"
          :total-rows="users.length"
          :per-page="perPage"
          first-number
          last-number
          aria-controls="users-table"
        />
      </div>
    </div>

    <div class="adm-empty" v-else>
      <div class="adm-empty-icon"><i class="fa fa-users" /></div>
      <div class="adm-empty-title">No app users</div>
      <div class="adm-empty-sub">
        Adjust the filter or wait for new users to register from the mobile app.
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "@/utils/apiBase";
import { customAxios } from "@/utils/customAxios";
import { getAuthItem, getAuthToken } from "@/utils/authStorage";
import { saveBlobResponse } from "@/utils/download";
import debounce from "lodash/debounce";
import DialogContainer from "@/components/DialogContainer.vue";

export default {
  components: {
    DialogContainer,
  },
  data() {
    return {
      confirmExport: false,
      isLoading: true,
      perPage: 20,
      currentPage: 1,
      exportToCsv: false,
      textInput: "",
      currentUserRole: parseInt(getAuthItem("userRole"), 10) || 0,
      roleOptions: [
        { value: "user", text: "User" },
        { value: "subject", text: "Subject" },
        { value: "researcher", text: "Researcher" },
      ],
      users: [],
      deleteTarget: null,
      cancelTokenSource: null,
    };
  },
  watch: {
    textInput: { handler: "debouncedFetchUsers", immediate: true },
  },
  computed: {
    isAdmin() {
      return this.currentUserRole === 1;
    },
    fields() {
      const base = [
        { key: "id", label: "ID" },
        { key: "email", label: "Email" },
        { key: "role", label: "Role" },
        { key: "is_active", label: "Status" },
      ];
      if (this.isAdmin) base.push({ key: "actions", label: "" });
      return base;
    },
  },
  methods: {
    roleLabel(role) {
      const opt = this.roleOptions.find((o) => o.value === (role || "user"));
      return opt ? opt.text : "User";
    },
    no() {
      this.confirmExport = false;
    },
    yes() {
      this.confirmExport = false;
      this.performExport();
    },
    exportBtn() {
      this.confirmExport = true;
    },
    performExport() {
      this.exportToCsv = true;
      this.cancelTokenSource = axios.CancelToken.source();
      axios
        .get(`${API_BASE_URL}/export-to-csv`, {
          params: { filter: "", search: this.textInput },
          cancelToken: this.cancelTokenSource.token,
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          responseType: "blob",
        })
        .then((response) => {
          // Despite the route name, AppUsersController::exportUsers returns
          // Excel::download(new AppUsersExport, 'app_users.xlsx') — a real xlsx
          // (the export applies bold headings and column widths, which csv
          // can't carry). Labelling it .csv hands the user a mislabelled
          // binary. Prefers the server's Content-Disposition when exposed.
          saveBlobResponse(response, "app_users.xlsx");
          this.exportToCsv = false;
        })
        .catch((error) => {
          console.error("Export failed", error);
        });
    },
    fetchUsers() {
      this.isLoading = true;
      customAxios
        .post("/users", { filter: "", search: this.textInput })
        .then((response) => {
          this.users = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => {
          console.error("Failed to load users", error);
          this.users = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async deactivateUser(user) {
      try {
        await customAxios.post(`/app-users/${user.id}/deactivate`);
        user.is_active = false;
      } catch (err) {
        console.error("Deactivate failed", err);
        alert("Failed to deactivate user.");
      }
    },
    async activateUser(user) {
      try {
        await customAxios.post(`/app-users/${user.id}/activate`);
        user.is_active = true;
      } catch (err) {
        console.error("Activate failed", err);
        alert("Failed to activate user.");
      }
    },
    confirmDeleteUser(user) {
      this.deleteTarget = user;
    },
    async performDelete() {
      const target = this.deleteTarget;
      if (!target) return;
      try {
        await customAxios.post(`/app-users/${target.id}/delete`);
        this.users = this.users.filter((u) => u.id !== target.id);
        this.deleteTarget = null;
      } catch (err) {
        console.error("Delete failed", err);
        alert("Failed to delete user.");
      }
    },
    async changeRole(user, newRole) {
      if (newRole === user.role) return;
      this.$set(user, "_roleSaving", true);
      const prevRole = user.role;
      try {
        const res = await customAxios.post(`/app-users/${user.id}/role`, {
          role: newRole,
        });
        user.role = (res.data && res.data.user && res.data.user.role) || newRole;
      } catch (err) {
        const data = err.response && err.response.data;
        const msg =
          (data && data.errors && Object.values(data.errors).flat()[0]) ||
          (data && data.message) ||
          err.message ||
          "Role update failed";
        user.role = prevRole;
        alert(msg);
      } finally {
        this.$set(user, "_roleSaving", false);
      }
    },
    debouncedFetchUsers: debounce(function () {
      this.fetchUsers();
    }, 300),
    toggleExportDialog() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel("Export canceled by user.");
      }
      this.exportToCsv = false;
    },
  },
};
</script>

<style scoped>
.search-input {
  flex: 1;
  min-width: 12rem;
  max-width: 24rem;
}

.role-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}
.role-dropdown >>> button.role-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 30px;
  padding: 0 0.75rem;
  background: #f7f8fb;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  color: #1f2540;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.role-dropdown >>> button.role-trigger:hover:not(:disabled) {
  background: #ffffff;
  border-color: #d8dde9;
}
.role-dropdown.show >>> button.role-trigger {
  background: #ffffff;
  border-color: #2a345d;
  box-shadow: 0 0 0 3px rgba(42, 52, 93, 0.12);
}
.role-dropdown >>> button.role-trigger:disabled {
  background: #f1f2f6;
  color: #98a0ad;
}
.role-caret {
  font-size: 0.7rem;
  color: #98a0ad;
  transition: transform 0.15s;
}
.role-dropdown.show >>> .role-caret {
  transform: rotate(180deg);
}
.role-saving {
  color: #2a345d;
}

/* Trigger + caret styling now lives in admin-theme.css (shared with the
   Accounts table). */

/* dialogs */
.adm-dialog {
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  max-width: 480px;
  text-align: center;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
}
.adm-dialog h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2540;
}
.adm-dialog p {
  color: #5b6577;
  font-size: 0.9rem;
}
.adm-dialog-icon {
  font-size: 2.5rem;
  color: #2a345d;
  margin-bottom: 0.5rem;
}
.adm-dialog-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 1rem;
}
.adm-btn-primary.danger {
  background: #b02a37;
  border-color: #b02a37;
}
.adm-btn-primary.danger:hover {
  background: #8a1f2a;
  border-color: #8a1f2a;
}

.loader {
  width: 40px;
  height: 40px;
  margin-bottom: 0.5rem;
}
</style>
